export const MARKETPLACE_TURNAROUND_PRESETS = ['24h', '72h', '1w'] as const;

export type MarketplaceProfileTurnaroundPreset = (typeof MARKETPLACE_TURNAROUND_PRESETS)[number];

/** Legacy values still accepted when reading older profiles. */
export type MarketplaceProfileTurnaroundLegacy = '2d' | '3d' | '5d' | '2w' | 'other';

/**
 * Single stored turnaround value: preset (`24h`, `72h`, `1w`) or custom encoded as `{n}d` / `{n}w`.
 */
export type MarketplaceProfileTurnaround =
  | MarketplaceProfileTurnaroundPreset
  | MarketplaceProfileTurnaroundLegacy
  | string;

const CUSTOM_ENCODED = /^(\d+)(d|w)$/;

export type TurnaroundCustomParts = {
  amount: number;
  unit: 'days' | 'weeks';
};

export function isTurnaroundPreset(
  value: string | undefined | null
): value is MarketplaceProfileTurnaroundPreset {
  return (
    value != null &&
    (MARKETPLACE_TURNAROUND_PRESETS as readonly string[]).includes(value)
  );
}

export function isTurnaroundCustomEncoded(value: string | undefined | null): boolean {
  if (!value) return false;
  const m = value.match(CUSTOM_ENCODED);
  if (!m) return false;
  const amount = parseInt(m[1], 10);
  return Number.isFinite(amount) && amount >= 1;
}

export function parseTurnaroundCustom(value: string): TurnaroundCustomParts | null {
  const m = value.match(CUSTOM_ENCODED);
  if (!m) return null;
  const amount = parseInt(m[1], 10);
  if (!Number.isFinite(amount) || amount < 1) return null;
  return { amount, unit: m[2] === 'w' ? 'weeks' : 'days' };
}

export function encodeTurnaroundCustom(parts: TurnaroundCustomParts): string {
  const amount = Math.max(1, Math.floor(parts.amount));
  return `${amount}${parts.unit === 'weeks' ? 'w' : 'd'}`;
}

export function isTurnaroundOtherUi(value: string | null | undefined): boolean {
  if (!value) return false;
  if (value === 'other') return true;
  // Note: presets like `1w` match the custom regex, but should NOT trigger the "Other" UI.
  if (isTurnaroundPreset(value)) return false;
  return isTurnaroundCustomEncoded(value);
}

/** Normalize API/Firestore values (including legacy two-field shape) to a single string. */
export function normalizeTurnaroundTime(
  turnaroundTime: string | undefined,
  legacyCustom?: TurnaroundCustomParts | null
): string | null {
  if (!turnaroundTime) return null;

  if (turnaroundTime === 'other') {
    if (legacyCustom && legacyCustom.amount >= 1) {
      return encodeTurnaroundCustom(legacyCustom);
    }
    return '1d';
  }

  if (turnaroundTime === '3d') {
    return '72h';
  }

  if (isTurnaroundPreset(turnaroundTime) || isTurnaroundCustomEncoded(turnaroundTime)) {
    return turnaroundTime;
  }

  if (['2d', '5d', '2w'].includes(turnaroundTime)) {
    return turnaroundTime;
  }

  return null;
}

export function formatTurnaroundLabel(turnaroundTime: string | null | undefined): string | null {
  if (!turnaroundTime) return null;
  switch (turnaroundTime) {
    case '24h':
      return '24 hours';
    case '72h':
      return '72 hours';
    case '1w':
      return '1 week';
    default: {
      const custom = parseTurnaroundCustom(turnaroundTime);
      if (!custom) return null;
      const n = custom.amount;
      const unitLabel =
        custom.unit === 'weeks' ? (n === 1 ? 'week' : 'weeks') : n === 1 ? 'day' : 'days';
      return `${n} ${unitLabel}`;
    }
  }
}

export function isValidTurnaroundTime(value: unknown): boolean {
  if (typeof value !== 'string' || !value.trim()) return false;
  const t = value.trim();
  if (isTurnaroundPreset(t)) return true;
  if (isTurnaroundCustomEncoded(t)) return true;
  return ['2d', '3d', '5d', '2w'].includes(t);
}

export function isTurnaroundSelectionComplete(turnaroundTime: string | null | undefined): boolean {
  if (!turnaroundTime) return false;
  return isValidTurnaroundTime(turnaroundTime);
}
